import { Injectable } from '@angular/core';
import * as Papa from 'papaparse';
import { Observable } from 'rxjs';

import {
  CSV_PARSE_DEFAULTS,
  CsvParseOptions,
  CsvParseResult,
  CsvParseWorkerRequest,
  CsvParseWorkerResponse,
  toCsvParseResult
} from './csv-parse';

export { detectDelimiter } from './csv-parse';
export type { CsvParseOptions, CsvParseResult } from './csv-parse';

@Injectable({
  providedIn: 'root'
})
export class CsvParserService {
  /**
   * Parses off the main thread when the browser supports workers, so a large file does not
   * freeze the page. Unsubscribing terminates the worker.
   */
  parseFile<T = Record<string, unknown>>(file: File): Observable<CsvParseResult<T>> {
    if (typeof Worker === 'undefined') {
      return this.parseFileOnMainThread<T>(file);
    }

    return new Observable((observer) => {
      const worker = new Worker(new URL('./csv-parse.worker', import.meta.url), {
        type: 'module'
      });
      worker.onmessage = ({ data }: MessageEvent<CsvParseWorkerResponse>) => {
        if (data.ok) {
          observer.next(data.result as CsvParseResult<T>);
          observer.complete();
        } else {
          observer.error(new Error(data.message));
        }
      };
      // Most often a worker that never started — its chunk gone after a redeploy, or blocked
      // by a CSP — and the error event then carries no message, so parse here instead.
      worker.onerror = (event) => {
        event.preventDefault();
        worker.terminate();
        this.parseFileOnMainThread<T>(file).subscribe(observer);
      };
      worker.postMessage({ file } satisfies CsvParseWorkerRequest);

      return () => worker.terminate();
    });
  }

  private parseFileOnMainThread<T>(file: File): Observable<CsvParseResult<T>> {
    return new Observable((observer) => {
      Papa.parse<T>(file, {
        ...CSV_PARSE_DEFAULTS,
        complete: (results: Papa.ParseResult<T>) => {
          try {
            const parseResult = toCsvParseResult<T>(results);
            observer.next(parseResult);
            observer.complete();
          } catch (error) {
            observer.error(error);
          }
        },
        error: (error: Error) => {
          observer.error(error);
        }
      } as Papa.ParseLocalConfig<T, File>);
    });
  }

  parseString<T = Record<string, unknown>>(
    csvString: string,
    options?: CsvParseOptions
  ): Observable<CsvParseResult<T>> {
    return new Observable((observer) => {
      try {
        const results = Papa.parse<T>(csvString, {
          ...CSV_PARSE_DEFAULTS,
          ...options
        } as Papa.ParseConfig<T>);

        const parseResult = toCsvParseResult<T>(results);
        observer.next(parseResult);
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }
}
